import React, { useState, useRef } from 'react';
import {
  Box,
  Avatar,
  Button,
  IconButton,
  Typography,
} from '@mui/material';
import {
  PhotoCamera,
  Delete as DeleteIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

interface AvatarUploadProps {
  value?: string | null;
  onChange: (base64: string | null) => void;
  disabled?: boolean;
}

const AvatarUpload: React.FC<AvatarUploadProps> = ({ value, onChange, disabled }) => {
  const [preview, setPreview] = useState<string | null>(value || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Ridimensiona l'immagine a 200x200 e converte in Base64
  const resizeImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Dimensione massima 200x200
          const maxSize = 200;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;

          ctx?.drawImage(img, 0, 0, width, height);

          // Converte in Base64 con qualità 0.8
          const base64 = canvas.toDataURL('image/jpeg', 0.8);
          resolve(base64);
        };
        img.onerror = reject;
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Valida tipo file
    if (!file.type.startsWith('image/')) {
      alert('Seleziona un file immagine valido');
      return;
    }

    // Valida dimensione (max 5MB prima del ridimensionamento)
    if (file.size > 5 * 1024 * 1024) {
      alert('Il file è troppo grande. Massimo 5MB');
      return;
    }

    try {
      const base64 = await resizeImage(file);
      setPreview(base64);
      onChange(base64);
    } catch (error) {
      console.error('Error processing image:', error);
      alert('Errore nel caricamento dell\'immagine');
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Box sx={{ position: 'relative' }}>
        <Avatar
          src={preview || undefined}
          sx={{
            width: 120,
            height: 120,
            border: '3px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        >
          {!preview && <PersonIcon sx={{ fontSize: 60 }} />}
        </Avatar>

        {preview && !disabled && (
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bgcolor: 'error.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'error.dark',
              },
            }}
            onClick={handleRemove}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={disabled}
      />

      <Button
        variant="outlined"
        startIcon={<PhotoCamera />}
        onClick={handleClick}
        disabled={disabled}
        size="small"
      >
        {preview ? 'Cambia Foto' : 'Carica Foto'}
      </Button>

      <Typography variant="caption" color="text.secondary" align="center">
        Formato: JPG, PNG, GIF (max 5MB)
        <br />
        Ridimensionata automaticamente a 200x200px
      </Typography>
    </Box>
  );
};

export default AvatarUpload;
